import React from 'react';
import CAppPaper from '../../../../../../../app/components/CAppPaper';
import { Box, Divider, Grid, Stack, SxProps, useTheme } from '@mui/material';
import { Link } from 'react-router-dom';
import HAppRoutes from '../../../../../../../app/features/routes/helpers/HAppRoutes';
import InvestorRoutes from '../../../../../InvestorRoutes';
import AppCustomIcon from '../../../../../../../app/features/icon/AppIcon';
import { useTranslation } from 'react-i18next';
import investExpertiseListedAssetsLang from '../../../../../langs/investExpertiseListedAssetsLang';
import styleVariables from '../../../../../../../app/styles/styleVariables';
import AppFormatAmount from '../../../../../../../app/features/format/AppFormatAmount';
import AppFormatPercent from '../../../../../../../app/features/format/AppFormatPercent';
import useInvestFunds from '../../../../../hooks/fund/useInvestFunds';
import AppFormatDate from '../../../../../../../app/features/format/AppFormatDate';
import IInvestKpisByFundForSiennaGestion from '../../../../../interfaces/IInvestKpisByFundForSiennaGestion';
import moment from 'moment';
import { langLookup } from '../../../../../../../app/utils/langLookup';

type CInvestSynthesisListedAssetsFundProps = {
	investKpisByFund: IInvestKpisByFundForSiennaGestion;
};

const CInvestSynthesisListedAssetsFund = (props: CInvestSynthesisListedAssetsFundProps) => {
	const { investKpisByFund } = props;

	const { t } = useTranslation();
	const theme = useTheme();

	const { fundsByKey } = useInvestFunds();
	const fund = fundsByKey && investKpisByFund.fund ? fundsByKey[investKpisByFund.fund.id] : null;

	const sxTitle = {
		fontSize: '1rem',
		fontWeight: 600,
		margin: 0,
		padding: 0,
	} as SxProps;

	const sxHeader = {
		fontSize: '0.85rem',
		fontWeight: 500,
		marginBottom: '10px',
	} as SxProps;

	const sxHeaderSub = {
		fontSize: '0.8rem',
		fontWeight: 500,
		color: styleVariables.greyColor,
	} as SxProps;

	const sxValueSmall = {
		fontSize: '0.85rem',
		fontWeight: 400,
	} as SxProps;

	const sxValueLarge = {
		fontSize: '1.25rem',
		fontWeight: 600,
	} as SxProps;

	const sxImgLabels = {
		height: '50px',
		width: '50px',
	} as SxProps;

	// Date of the performance are the same for all, we take the first defined
	const performanceDate = investKpisByFund.kpi?.synthesisCumulativePerformance?.length
		? investKpisByFund.kpi.synthesisCumulativePerformance.filter((p) => p.value !== null)[0]?.dateCalcul
		: null;

	const formattedPerformanceDate = performanceDate ? moment(performanceDate).format('DD/MM/YYYY') : 'n.c.';

	const kpiFund = investKpisByFund?.fund;
	return (
		<>
			{kpiFund && fund ? (
				<>
					<CAppPaper variant="outlined" sx={{ padding: '0px', marginBottom: '16px' }}>
						<Box sx={{ padding: '16px 12px' }}>
							<Stack direction="row" justifyContent="space-between" alignItems="center">
								{[
									<Box key={kpiFund.name} sx={sxTitle}>
										{kpiFund.name}
									</Box>,
									<>
										<AppCustomIcon tag="arrow-right" style={{ verticalAlign: 'middle' }} />
										{t(investExpertiseListedAssetsLang.go_to_fund.tag)}
									</>,
								].map((item, index) => (
									<Box key={index}>
										<Link
											color="primary"
											to={HAppRoutes.getPath(InvestorRoutes.fund.tag, {
												investExpertiseTag: fund.typeTag ?? undefined,
												id: kpiFund.id.toString(),
											})}
											style={{
												textDecoration: 'none',

												fontFamily: "'Poppins'",
												fontStyle: 'normal',
												fontWeight: '500',
												fontSize: '0.9rem',

												display: 'inline-block',
												color:
													theme.palette.mode === 'dark' ? '#FFF' : theme.palette.primary.main,
											}}
										>
											{item}
										</Link>
									</Box>
								))}
							</Stack>
						</Box>
						<Divider />
						<Box sx={{ padding: '0px' }}>
							<Grid container alignItems="top" justifyContent="center">
								<Grid
									item
									sm={1.5}
									xs={4}
									sx={{
										borderLeft: '0px solid',
										padding: '14px 12px',
									}}
								>
									<Box sx={sxHeader}>{t(investExpertiseListedAssetsLang.subtitle_portfolio.tag)}</Box>
									<Box sx={sxHeaderSub}>
										{t(investExpertiseListedAssetsLang.header_total_net_assets.tag)}
									</Box>
									<Box sx={sxValueLarge}>
										<AppFormatAmount
											value={investKpisByFund.kpi?.aum ?? undefined}
											currency={investKpisByFund.fund?.currency}
										/>
									</Box>
								</Grid>
								<Grid
									item
									sm={6.5}
									xs={8}
									sx={{
										borderLeft: '1px solid',
										borderLeftColor: styleVariables.greyLightColor,
										padding: '14px 12px',
									}}
								>
									<Box sx={sxHeader}>
										{t(investExpertiseListedAssetsLang.subtitle_fund_performance.tag, {
											date: formattedPerformanceDate,
										})}
									</Box>
									<Grid container spacing={3}>
										<Grid item sm={1.5} xs={4}>
											<Box
												sx={{
													borderRight: '1px solid #E0E0E0',
												}}
											>
												<Box sx={sxHeaderSub}>
													{t(investExpertiseListedAssetsLang.header_vl.tag)}
												</Box>
												<Box sx={sxValueSmall}>
													<AppFormatAmount
														value={investKpisByFund.kpi?.navPerShare ?? undefined}
														currency={investKpisByFund.fund?.currency}
														customFloatDigitLimit={3}
														disableIntegerRounding
													/>
												</Box>
											</Box>
										</Grid>
										{investKpisByFund.kpi?.synthesisCumulativePerformance?.map((p) => (
											<Grid key={p.dateType} item sm={1.75} xs={4}>
												<Box sx={sxHeaderSub}>
													{t(
														langLookup(investExpertiseListedAssetsLang, p.dateType)?.tag ??
															'',
													)}
												</Box>
												<Box sx={sxValueSmall}>
													<AppFormatPercent value={p.value} textIfNull="n.c." displayPlus />
												</Box>
											</Grid>
										))}
									</Grid>
								</Grid>
								<Grid
									item
									sm={4}
									xs={12}
									sx={{
										borderLeft: '1px solid',
										borderLeftColor: styleVariables.greyLightColor,
										padding: '14px 12px',
									}}
								>
									<Grid container spacing={3} alignItems="top" justifyContent="center">
										<Grid item sm={8} xs={8}>
											<Box sx={sxHeader}>
												{t(investExpertiseListedAssetsLang.subtitle_fund_characteristics.tag)}
											</Box>
											<Grid container spacing={3}>
												<Grid item sm={6} xs={12}>
													<Box sx={sxHeaderSub}>
														{t(investExpertiseListedAssetsLang.header_classification.tag)}
													</Box>
													<Box sx={sxValueSmall}>{fund?.classificationAMF}</Box>
												</Grid>
												{fund.creationDate ? (
													<Grid item sm={6} xs={12}>
														<Box sx={sxHeaderSub}>
															{t(
																investExpertiseListedAssetsLang.header_launching_date
																	.tag,
															)}
														</Box>
														<Box sx={sxValueSmall}>
															<AppFormatDate date={fund.creationDate} />
														</Box>
													</Grid>
												) : (
													<Grid item sm={6} xs={12}>
														<Box sx={sxHeaderSub}>
															{t(
																investExpertiseListedAssetsLang.header_agreement_date
																	.tag,
															)}
														</Box>
														<Box sx={sxValueSmall}>
															<AppFormatDate date={fund.accreditationDate} />
														</Box>
													</Grid>
												)}
											</Grid>
										</Grid>
										<Grid item sm={4} xs={4}>
											<Grid container spacing={0} alignItems="center" justifyContent="center">
												<Grid item xs={6}>
													{fund.labels && fund.labels.includes('cies') ? (
														<Box
															component="img"
															src="/images/labels/label_cies.png"
															sx={sxImgLabels}
														/>
													) : (
														''
													)}
												</Grid>
												<Grid item xs={6}>
													{fund.labels && fund.labels.includes('finansol') ? (
														<Box
															component="img"
															src="/images/labels/label_finansol.png"
															sx={sxImgLabels}
														/>
													) : (
														''
													)}
												</Grid>
												<Grid item xs={6}>
													{fund.labels && fund.labels.includes('greenfin') ? (
														<Box
															component="img"
															src="/images/labels/label_greenfin.png"
															sx={sxImgLabels}
														/>
													) : (
														''
													)}
												</Grid>
												<Grid item xs={6}>
													{fund.labels && fund.labels.includes('isr') ? (
														<Box
															component="img"
															src="/images/labels/label_isr.png"
															sx={sxImgLabels}
														/>
													) : (
														''
													)}
												</Grid>
											</Grid>
										</Grid>
									</Grid>
								</Grid>
							</Grid>
						</Box>
					</CAppPaper>
				</>
			) : (
				<></>
			)}
		</>
	);
};

export default CInvestSynthesisListedAssetsFund;
